import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McInput } from "@maersk-global/mds-react-wrapper";
import { McTabBar } from "@maersk-global/mds-react-wrapper/components-core/mc-tab-bar";
import { McTab } from "@maersk-global/mds-react-wrapper/components-core/mc-tab";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { useMsal } from "@azure/msal-react";
import apiClient from "../api/apiClient";
import * as XLSX from "xlsx";
import { checkUserIsAdmin } from "../api/adminUserCheck";
import { apiUrl } from "../authconfig";

import "@maersk-global/mds-components-core-table";
import "@maersk-global/mds-components-core/mc-input";

const PENDING_COUNT_STORAGE_KEY = "dealtoreal_user_access_pending_count";

const API_BASE_URL = String(apiUrl || "").replace(/\/$/, "");

const ACCESS_API_BASE = `${API_BASE_URL}/api/user-access`;

/** GET list of assigned users (distinct principals). */
const ASSIGN_USERS_API = `${API_BASE_URL}/api/assign-users`;

/** Base URL for POST /api/admin-user/promote/{userId}. */
const ADMIN_PROMOTE_API_BASE = API_BASE_URL;

function formatDateTime(value) {
  if (!value) return "";
  try {
    // Ensure consistent parsing by treating ambiguous strings as UTC
    const dateStr = String(value).trim();
    const date = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00Z');
    if (Number.isNaN(date.getTime())) return "";
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  } catch (e) {
    return "";
  }
}

function mapAssignUserRow(item) {
  return {
    id: String(item?.id ?? ""),
    displayName: item?.displayName ?? "",
    userPrincipalName: item?.userPrincipalName ?? "",
    userType: item?.userType ?? "",
    objectType: item?.objectType ?? "",
  };
}

function mapRequest(item) {
  return {
    id: String(item?.id ?? ""),
    requestId: String(item?.id ?? ""),
    displayName: item?.displayName ?? "",
    jobTitle: item?.userType ?? "",
    userPrincipalName: item?.userPrincipalName ?? "",
    businessJustification: item?.comments ?? "",
    status: item?.status ?? "",
    requestedDateRaw: item?.requestedDate ?? "",
    requestedDate: formatDateTime(item?.requestedDate),
    reviewedDate: formatDateTime(item?.reviewedDate),
    reviewedBy: item?.reviewedBy ?? "",
    rejectionReason: item?.comments ?? "",
  };
}

function toRequestIds(source) {
  if (source == null) return [];

  if (Array.isArray(source)) {
    return [...new Set(source.flatMap((entry) => toRequestIds(entry)).filter(Boolean))];
  }

  if (typeof source === "string" || typeof source === "number") {
    return [String(source)];
  }

  if (typeof source !== "object") return [];

  if (source.id != null || source.requestId != null || source.requestid != null) {
    return [String(source.id ?? source.requestId ?? source.requestid)];
  }

  const selectionKeys = [
    "selectedIds",
    "selectedRows",
    "selectedrows",
    "selectedItems",
    "selection",
    "selected",
    "rows",
    "items",
    "value",
    "detail",
  ];
  for (const key of selectionKeys) {
    const ids = toRequestIds(source[key]);
    if (ids.length > 0) return ids;
  }

  return [];
}

function getIdsFromTableElement(tableEl) {
  if (!tableEl) return [];

  const candidates = [
    tableEl.selectedIds,
    tableEl.selectedRows,
    tableEl.selectedrows,
    tableEl.selectedItems,
    tableEl.selection,
    tableEl.selected,
    tableEl.value,
  ];

  if (typeof tableEl.getSelectedRows === "function") {
    candidates.push(tableEl.getSelectedRows());
  }
  if (typeof tableEl.getSelectedItems === "function") {
    candidates.push(tableEl.getSelectedItems());
  }

  for (const candidate of candidates) {
    const ids = toRequestIds(candidate);
    if (ids.length > 0) return ids;
  }

  return [];
}

const ASSIGN_USERS_PAGE_SIZE = 15;

function downloadAssignUsersExcel(users) {
  const rows = users.map((row) => ({
    ID: row.id,
    Name: row.displayName,
    Email: row.userPrincipalName,
    "User type": row.userType,
    "Object type": row.objectType,
  }));
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Assign users");
  XLSX.writeFile(
    workbook,
    `dealtoreal-assign-users-${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}

/** McTabBar tabchange detail shape varies; McTab onClick is the reliable sync. */
function parseTabIndexFromEvent(event) {
  const raw = event?.detail ?? event?.nativeEvent?.detail;
  if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
  if (raw && typeof raw === "object") {
    if (typeof raw.index === "number") return raw.index;
    if (typeof raw.selectedIndex === "number") return raw.selectedIndex;
  }
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

export default function UserAccess() {
  const navigate = useNavigate();
  const { accounts } = useMsal();

  const currentEmail = (
    accounts?.[0]?.idTokenClaims?.email ||
    accounts?.[0]?.username ||
    ""
  ).toLowerCase();

  const [accessCheck, setAccessCheck] = useState({
    status: "loading",
    isAdmin: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentEmail) {
        if (!cancelled) {
          setAccessCheck({ status: "ready", isAdmin: false, error: null });
        }
        return;
      }
      if (!cancelled) {
        setAccessCheck({ status: "loading", isAdmin: false, error: null });
      }
      const result = await checkUserIsAdmin(currentEmail);
      if (cancelled) return;
      setAccessCheck({
        status: "ready",
        isAdmin: result.isAdmin,
        error: result.error,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [currentEmail]);

  useEffect(() => {
    if (accessCheck.status !== "ready") return;
    if (!accessCheck.isAdmin) {
      navigate("/", { replace: true });
    }
  }, [accessCheck.status, accessCheck.isAdmin, navigate]);

  const isAllowed = accessCheck.status === "ready" && accessCheck.isAdmin;

  const [tabIndex, setTabIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [showRejectCommentBox, setShowRejectCommentBox] = useState(false);
  const [pendingRejectMode, setPendingRejectMode] = useState(null);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignUsersLoading, setAssignUsersLoading] = useState(false);
  const [currentUsersError, setCurrentUsersError] = useState(null);
  const [currentUsersSuccess, setCurrentUsersSuccess] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [adminSelectedRows, setAdminSelectedRows] = useState([]);
  const [assignUsersPage, setAssignUsersPage] = useState(1);
  const tableWrapperRef = useRef(null);
  const adminTableWrapperRef = useRef(null);

  useEffect(() => {
    const count = pending.length;
    sessionStorage.setItem(PENDING_COUNT_STORAGE_KEY, String(count));
    window.dispatchEvent(
      new CustomEvent("dtr-user-access-pending-count", { detail: { count } })
    );
  }, [pending.length]);

  const adminEmail = useMemo(() => {
    const accountEmail =
      accounts?.[0]?.idTokenClaims?.email ||
      accounts?.[0]?.username ||
      "";
    return String(accountEmail || "System");
  }, [accounts]);

  const setListsFromRequests = useCallback((requests) => {
    const list = Array.isArray(requests) ? requests.map(mapRequest) : [];
    setPending(list.filter((item) => String(item.status).toUpperCase() === "PENDING"));
    setApproved(list.filter((item) => String(item.status).toUpperCase() === "APPROVED"));
    setRejected(list.filter((item) => String(item.status).toUpperCase() === "REJECTED"));
  }, []);

  const fetchData = useCallback(async (options) => {
    const background = Boolean(options?.background);
    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      const { data } = await apiClient.get(`${ACCESS_API_BASE}/all-requests`);
      setListsFromRequests(data?.requests);
      setSelectedRows([]);
      setAdminSelectedRows([]);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load user access requests";
      if (!background) {
        setError(message);
        setPending([]);
        setApproved([]);
        setRejected([]);
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [setListsFromRequests]);

  const fetchAssignUsers = useCallback(async () => {
    setAssignUsersLoading(true);
    setCurrentUsersError(null);
    try {
      const { data } = await apiClient.get(ASSIGN_USERS_API);
      const rows = Array.isArray(data) ? data : [];
      setAssignUsers(rows.map(mapAssignUserRow).filter((row) => row.id));
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load assigned users";
      setCurrentUsersError(message);
      setAssignUsers([]);
    } finally {
      setAssignUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAssignUsers();
  }, [fetchAssignUsers]);

  const TAB_PENDING = 0;
  const TAB_CURRENT_USERS = 1;
  const TAB_APPROVED = 2;
  const TAB_REJECTED = 3;

  const setSelectionFromEvent = useCallback((event) => {
    const tableEl =
      event?.target?.tagName?.toLowerCase?.() === "mc-table"
        ? event.target
        : tableWrapperRef.current?.querySelector("mc-table");
    const idsFromDetail = toRequestIds(event?.detail);
    const idsFromTable = getIdsFromTableElement(tableEl);
    const ids = idsFromDetail.length > 0 ? idsFromDetail : idsFromTable;
    setSelectedRows(ids.map((id) => ({ id })));
  }, []);

  const setAdminSelectionFromEvent = useCallback((event) => {
    const tableEl =
      event?.target?.tagName?.toLowerCase?.() === "mc-table"
        ? event.target
        : adminTableWrapperRef.current?.querySelector("mc-table");
    const idsFromDetail = toRequestIds(event?.detail);
    const idsFromTable = getIdsFromTableElement(tableEl);
    const ids = idsFromDetail.length > 0 ? idsFromDetail : idsFromTable;
    setAdminSelectedRows(ids.map((id) => ({ id })));
  }, []);

  useEffect(() => {
    const tableEl = tableWrapperRef.current?.querySelector("mc-table");
    if (!tableEl || tabIndex !== TAB_PENDING) return undefined;

    const handler = (event) => setSelectionFromEvent(event);
    tableEl.addEventListener("selectchange", handler);
    tableEl.addEventListener("mc-selectchange", handler);

    return () => {
      tableEl.removeEventListener("selectchange", handler);
      tableEl.removeEventListener("mc-selectchange", handler);
    };
  }, [setSelectionFromEvent, pending.length, tabIndex]);

  useEffect(() => {
    const tableEl = adminTableWrapperRef.current?.querySelector("mc-table");
    if (!tableEl || tabIndex !== TAB_CURRENT_USERS) return undefined;

    const handler = (event) => setAdminSelectionFromEvent(event);
    tableEl.addEventListener("selectchange", handler);
    tableEl.addEventListener("mc-selectchange", handler);

    return () => {
      tableEl.removeEventListener("selectchange", handler);
      tableEl.removeEventListener("mc-selectchange", handler);
    };
  }, [setAdminSelectionFromEvent, assignUsers.length, tabIndex]);

  useEffect(() => {
    if (tabIndex !== TAB_PENDING) {
      setSelectedRows([]);
      setShowRejectCommentBox(false);
      setPendingRejectMode(null);
      setRejectComment("");
    }
    if (tabIndex !== TAB_CURRENT_USERS) {
      setAdminSelectedRows([]);
      setCurrentUsersError(null);
      setCurrentUsersSuccess(null);
    }
    if (tabIndex !== TAB_PENDING && tabIndex !== TAB_CURRENT_USERS) {
      setError(null);
    }
  }, [tabIndex]);

  const selectedIds = selectedRows
    .map((row) => (typeof row === "object" && row?.id != null ? row.id : row))
    .filter(Boolean);

  const getSelectedIds = useCallback(() => {
    if (selectedIds.length > 0) return [...new Set(selectedIds)];
    const tableEl = tableWrapperRef.current?.querySelector("mc-table");
    return getIdsFromTableElement(tableEl);
  }, [selectedIds]);

  const adminSelectedIds = adminSelectedRows
    .map((row) => (typeof row === "object" && row?.id != null ? row.id : row))
    .filter(Boolean);

  const getAdminSelectedIds = useCallback(() => {
    if (adminSelectedIds.length > 0) return [...new Set(adminSelectedIds)];
    const tableEl = adminTableWrapperRef.current?.querySelector("mc-table");
    return getIdsFromTableElement(tableEl);
  }, [adminSelectedIds]);

  const handleExportAssignUsers = useCallback(() => {
    if (!assignUsers.length) {
      setCurrentUsersError("No users loaded yet — nothing to export.");
      setCurrentUsersSuccess(null);
      return;
    }
    setCurrentUsersError(null);
    downloadAssignUsersExcel(assignUsers);
    setCurrentUsersSuccess(`Exported ${assignUsers.length} user(s) to Excel.`);
  }, [assignUsers]);

  const handlePromoteToAdmin = useCallback(() => {
    const run = async () => {
      const ids = getAdminSelectedIds();
      if (ids.length === 0) {
        setCurrentUsersError("Select one or more users (checkboxes), then click Promote to admin.");
        setCurrentUsersSuccess(null);
        return;
      }
      setCurrentUsersError(null);
      setCurrentUsersSuccess(null);
      setSearch("");
      setActionLoading("promote-admin");
      try {
        for (const id of ids) {
          await apiClient.post(
            `${ADMIN_PROMOTE_API_BASE.replace(/\/$/, "")}/api/admin-user/promote/${encodeURIComponent(id)}`,
            { promotedBy: adminEmail },
            { headers: { "Content-Type": "application/json" } }
          );
        }
        setAdminSelectedRows([]);
        await Promise.all([fetchAssignUsers(), fetchData({ background: true })]);
        setCurrentUsersSuccess(
          ids.length === 1
            ? "User promoted to admin successfully."
            : `${ids.length} users promoted to admin successfully.`
        );
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to promote user(s) to admin";
        setCurrentUsersError(message);
      } finally {
        setActionLoading(null);
      }
    };
    run();
  }, [adminEmail, fetchAssignUsers, fetchData, getAdminSelectedIds]);

  const handleApproveSingle = useCallback(() => {
    const run = async () => {
    const ids = getSelectedIds();
    if (ids.length !== 1) {
      setError("Please select exactly one row to approve with single action.");
      return;
    }
    setError(null);
    setShowRejectCommentBox(false);
    setPendingRejectMode(null);
    setRejectComment("");
    setActionLoading("approve-single");
      try {
        await apiClient.post(`${ACCESS_API_BASE}/approve/${encodeURIComponent(ids[0])}`, {
          approvedBy: adminEmail,
        });
        await fetchData();
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to approve request";
        setError(message);
      } finally {
        setActionLoading(null);
      }
    };
    run();
  }, [adminEmail, fetchData, getSelectedIds]);

  const handleRejectSingle = useCallback(() => {
    const ids = getSelectedIds();
    if (ids.length !== 1) {
      setError("Please select exactly one row to reject with single action.");
      return;
    }
    setError(null);
    setPendingRejectMode("single");
    setShowRejectCommentBox(true);
  }, [getSelectedIds]);

  const handleApproveBulk = useCallback(() => {
    const run = async () => {
    const ids = getSelectedIds();
    if (ids.length < 2) {
      setError("Please select two or more rows to approve in bulk.");
      return;
    }
    setError(null);
    setShowRejectCommentBox(false);
    setPendingRejectMode(null);
    setRejectComment("");
    setActionLoading("approve-bulk");
      try {
        await apiClient.post(`${ACCESS_API_BASE}/approve-bulk`, {
          requestIds: ids,
          approvedBy: adminEmail,
        });
        await fetchData();
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to bulk approve requests";
        setError(message);
      } finally {
        setActionLoading(null);
      }
    };
    run();
  }, [adminEmail, fetchData, getSelectedIds]);

  const handleRejectBulk = useCallback(() => {
    const ids = getSelectedIds();
    if (ids.length < 2) {
      setError("Please select two or more rows to reject in bulk.");
      return;
    }
    setError(null);
    setPendingRejectMode("bulk");
    setShowRejectCommentBox(true);
  }, [getSelectedIds]);

  const handleConfirmReject = useCallback(() => {
    const run = async () => {
    const ids = getSelectedIds();
    if (pendingRejectMode === "single" && ids.length !== 1) {
      setError("Please select exactly one row to reject with single action.");
      return;
    }
    if (pendingRejectMode === "bulk" && ids.length < 2) {
      setError("Please select two or more rows to reject in bulk.");
      return;
    }
    if (!rejectComment.trim()) {
      setError("Please provide rejection reason/comments before rejecting.");
      return;
    }
    setError(null);
    setActionLoading(pendingRejectMode === "bulk" ? "reject-bulk" : "reject-single");
      try {
        if (pendingRejectMode === "bulk") {
          await apiClient.post(`${ACCESS_API_BASE}/reject-bulk`, {
            requestIds: ids,
            rejectedBy: adminEmail,
            rejectionReason: rejectComment.trim(),
          });
        } else {
          await apiClient.post(`${ACCESS_API_BASE}/reject/${encodeURIComponent(ids[0])}`, {
            rejectedBy: adminEmail,
            rejectionReason: rejectComment.trim(),
          });
        }
        setRejectComment("");
        setShowRejectCommentBox(false);
        setPendingRejectMode(null);
        await fetchData();
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to reject request(s)";
        setError(message);
      } finally {
        setActionLoading(null);
      }
    };
    run();
  }, [adminEmail, fetchData, getSelectedIds, pendingRejectMode, rejectComment]);

  const filterList = useCallback((list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (row) =>
        (row.displayName || "").toLowerCase().includes(q) ||
        (row.userPrincipalName || "").toLowerCase().includes(q) ||
          (row.jobTitle || "").toLowerCase().includes(q) ||
          (row.businessJustification || "").toLowerCase().includes(q)
    );
  }, [search]);

  /** Search applies only to the active tab so other tabs keep full rows (e.g. after promoting from Current Users). */
  const filteredPending = useMemo(() => {
    if (tabIndex !== TAB_PENDING) return pending;
    return filterList(pending);
  }, [tabIndex, pending, filterList]);

  const filteredApproved = useMemo(() => {
    if (tabIndex !== TAB_APPROVED) return approved;
    return filterList(approved);
  }, [tabIndex, approved, filterList]);

  const filteredRejected = useMemo(() => {
    if (tabIndex !== TAB_REJECTED) return rejected;
    return filterList(rejected);
  }, [tabIndex, rejected, filterList]);

  const selectionCount = selectedIds.length;
  const adminSelectionCount = adminSelectedIds.length;
  const actionHint =
    selectionCount === 0
      ? "Select row(s) from Pending requests to enable actions."
      : selectionCount === 1
        ? "1 row selected. Single actions are available."
        : `${selectionCount} rows selected. Bulk actions are available.`;
  const adminActionHint =
    adminSelectionCount === 0
      ? "Select user(s) in the table, then click Promote to admin."
      : adminSelectionCount === 1
        ? "1 user selected."
        : `${adminSelectionCount} users selected.`;

  const filteredAssignUsers = useMemo(() => {
    if (tabIndex !== TAB_CURRENT_USERS) return assignUsers;
    if (!search.trim()) return assignUsers;
    const q = search.toLowerCase();
    return assignUsers.filter(
      (row) =>
        (row.displayName || "").toLowerCase().includes(q) ||
        (row.userPrincipalName || "").toLowerCase().includes(q) ||
        (row.userType || "").toLowerCase().includes(q) ||
        (row.objectType || "").toLowerCase().includes(q)
    );
  }, [assignUsers, search, tabIndex]);

  const assignUsersTotalPages = Math.max(
    1,
    Math.ceil(filteredAssignUsers.length / ASSIGN_USERS_PAGE_SIZE)
  );

  const pagedAssignUsers = useMemo(() => {
    const start = (assignUsersPage - 1) * ASSIGN_USERS_PAGE_SIZE;
    return filteredAssignUsers.slice(start, start + ASSIGN_USERS_PAGE_SIZE);
  }, [filteredAssignUsers, assignUsersPage]);

  useEffect(() => {
    setAssignUsersPage(1);
  }, [search, assignUsers]);

  useEffect(() => {
    setAssignUsersPage((p) => Math.min(p, assignUsersTotalPages));
  }, [assignUsersTotalPages]);

  const assignUsersColumns = [
    { id: "displayName", label: "Name", width: "180px", noWrap: true },
    { id: "userPrincipalName", label: "Email", noWrap: true },
    { id: "userType", label: "User type", noWrap: true },
    { id: "objectType", label: "Object type", noWrap: true },
  ];

  const tableColumns = [
    { id: "displayName", label: "Name", width: "180px", noWrap: true },
    { id: "jobTitle", label: "Job Title", noWrap: true },
    { id: "userPrincipalName", label: "Email", noWrap: true },
    { id: "businessJustification", label: "Business Justification", noWrap: false },
    { id: "requestedDate", label: "Requested Date", noWrap: false },
  ];
  const rejectedTableColumns = [
    ...tableColumns,
    { id: "rejectionReason", label: "Rejection Comments", noWrap: false },
  ];

  const pageStyle = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: 24,
    fontFamily: '"Maersk Text", Arial, sans-serif',
  };

  if (accessCheck.status === "loading") {
    return (
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: 24, fontFamily: '"Maersk Text", Arial, sans-serif' }}>
        <p style={{ color: "#5b7785", fontSize: 14 }}>Checking access…</p>
      </div>
    );
  }

  if (!isAllowed) return null;

  return (
    <div style={pageStyle}>
      <div style={{ marginBottom: 16 }}>
        <McButton
          label="Back"
          appearance="neutral"
          variant="outlined"
          fit="medium"
          click={() => navigate("/")}
        />
      </div>
      <h1 style={{ margin: "0 0 16px 0", color: "#003e62", fontSize: 24 }}>User Access</h1>

      <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 280 }}>
          <McInput
            label="Search"
            icon="magnifying-glass"
            fit="small"
            value={search}
            input={(event) => setSearch(event?.target?.value ?? "")}
          />
        </div>
        {tabIndex === TAB_PENDING && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#4a4a4a" }}>{actionHint}</span>
            {selectionCount <= 1 ? (
              <>
                <McButton
                  label={actionLoading === "approve-single" ? "Approving one..." : "Approve one"}
                  appearance="primary"
                  fit="medium"
                  click={handleApproveSingle}
                />
                <McButton
                  label={actionLoading === "reject-single" ? "Rejecting one..." : "Reject one"}
                  appearance="error"
                  variant="filled"
                  fit="medium"
                  click={handleRejectSingle}
                />
              </>
            ) : (
              <>
                <McButton
                  label={actionLoading === "approve-bulk" ? "Approving many..." : "Approve bulk"}
                  appearance="primary"
                  variant="outlined"
                  fit="medium"
                  click={handleApproveBulk}
                />
                <McButton
                  label={actionLoading === "reject-bulk" ? "Rejecting many..." : "Reject bulk"}
                  appearance="error"
                  variant="outlined"
                  fit="medium"
                  click={handleRejectBulk}
                />
              </>
            )}
          </div>
        )}
      </div>
      {search.trim() !== "" && (
        <p style={{ fontSize: 12, color: "#5b7785", margin: "0 0 16px 0", maxWidth: 720 }}>
          Search applies only to the tab you are viewing. Switching tabs does not hide data in other queues unless you
          search while on that tab.
        </p>
      )}
      </div>
      <McTabBar
        variant="stretched"
        fit="small"
        currentindex={tabIndex}
        onTabchange={(event) => {
          const next = parseTabIndexFromEvent(event);
          if (next != null) setTabIndex(next);
        }}
      >
        <McTab
          slot="tab"
          label="Pending requests"
          icon="check-circle-solid"
          onClick={() => setTabIndex(TAB_PENDING)}
        >
          <span slot="suffix">
            <McTag appearance="error" fit="small" label={String(filteredPending.length)} />
          </span>
        </McTab>
        <div slot="panel">
          {error && (
            <div style={{ padding: 12, marginBottom: 14, background: "#fce4e8", borderRadius: 8, color: "#b8382d", fontSize: 14 }}>
              {error}
            </div>
          )}
          <p style={{ fontSize: 14, color: "#666", marginBottom: 12 }}>
            Select 1 row for single action, or 2+ rows for bulk action.
          </p>
          <div ref={tableWrapperRef}>
            {loading ? (
              <p style={{ fontSize: 14, color: "#666", margin: "12px 0" }}>Loading requests...</p>
            ) : (
              <McTable
                select
                datakey="id"
                data={filteredPending}
                columns={tableColumns}
                recordsperpage={20}
                headersticky
                selectsticky
                onSelectchange={setSelectionFromEvent}
              />
            )}
          </div>
        </div>

        <McTab
          slot="tab"
          label="Current Users list"
          icon="user-circle-solid"
          onClick={() => setTabIndex(TAB_CURRENT_USERS)}
        >
          <span slot="suffix">
            <McTag appearance="neutral" fit="small" label={String(filteredAssignUsers.length)} />
          </span>
        </McTab>
        <div slot="panel">
          <div
            style={{
              marginBottom: 16,
              padding: "12px 14px",
              background: "#eef6fb",
              border: "1px solid #c4d9e4",
              borderRadius: 8,
              fontSize: 13,
              color: "#003e62",
            }}
          >
            Select the users you want as admins in the table, then click <strong>Promote to admin</strong>.
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            <span style={{ fontSize: 13, color: "#4a4a4a", flex: "1 1 220px" }}>{adminActionHint}</span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <McButton
                label="Export full user list"
                appearance="neutral"
                variant="outlined"
                fit="medium"
                click={handleExportAssignUsers}
              />
              <McButton
                label={actionLoading === "promote-admin" ? "Promoting..." : "Promote to admin"}
                appearance="primary"
                variant="filled"
                fit="medium"
                click={handlePromoteToAdmin}
              />
            </div>
          </div>
          {currentUsersSuccess && (
            <div
              style={{
                padding: 12,
                marginBottom: 14,
                background: "#e8f5e9",
                borderRadius: 8,
                color: "#2e7d32",
                fontSize: 14,
              }}
            >
              {currentUsersSuccess}
            </div>
          )}
          {currentUsersError && (
            <div style={{ padding: 12, marginBottom: 14, background: "#fce4e8", borderRadius: 8, color: "#b8382d", fontSize: 14 }}>
              {currentUsersError}
            </div>
          )}
          
          <div ref={adminTableWrapperRef}>
            {assignUsersLoading ? (
              <p style={{ fontSize: 14, color: "#666", margin: "12px 0" }}>Loading users...</p>
            ) : (
              <>
                <McTable
                  select
                  datakey="id"
                  data={pagedAssignUsers}
                  columns={assignUsersColumns}
                  currentpage={0}
                  headersticky
                  selectsticky
                  onSelectchange={setAdminSelectionFromEvent}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    marginTop: 14,
                    paddingTop: 12,
                    borderTop: "1px solid #e8eef2",
                  }}
                >
                  <span style={{ fontSize: 13, color: "#5b7785" }}>
                    {filteredAssignUsers.length === 0
                      ? "No users to show."
                      : `Showing ${(assignUsersPage - 1) * ASSIGN_USERS_PAGE_SIZE + 1}–${Math.min(
                          assignUsersPage * ASSIGN_USERS_PAGE_SIZE,
                          filteredAssignUsers.length
                        )} of ${filteredAssignUsers.length} · Page ${assignUsersPage} of ${assignUsersTotalPages}`}
                  </span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <McButton
                      label="Previous page"
                      appearance="neutral"
                      variant="outlined"
                      fit="small"
                      click={() => {
                        setAssignUsersPage((p) => Math.max(1, p - 1));
                        setAdminSelectedRows([]);
                      }}
                    />
                    <McButton
                      label="Next page"
                      appearance="neutral"
                      variant="outlined"
                      fit="small"
                      click={() => {
                        setAssignUsersPage((p) => Math.min(assignUsersTotalPages, p + 1));
                        setAdminSelectedRows([]);
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <McTab
          slot="tab"
          label="Approved requests"
          icon="check-circle-solid"
          onClick={() => setTabIndex(TAB_APPROVED)}
        >
          <span slot="suffix">
            <McTag appearance="success" fit="small" label={String(filteredApproved.length)} />
          </span>
        </McTab>
        <div slot="panel">
          {loading ? (
            <p style={{ fontSize: 14, color: "#666", margin: "12px 0" }}>Loading requests...</p>
          ) : (
            <McTable datakey="id" data={filteredApproved} columns={tableColumns} recordsperpage={20} headersticky />
          )}
        </div>

        <McTab
          slot="tab"
          label="Rejected requests"
          icon="times-circle-solid"
          onClick={() => setTabIndex(TAB_REJECTED)}
        >
          <span slot="suffix">
            <McTag appearance="warning" fit="small" label={String(filteredRejected.length)} />
          </span>
        </McTab>
        <div slot="panel">
          {loading ? (
            <p style={{ fontSize: 14, color: "#666", margin: "12px 0" }}>Loading requests...</p>
          ) : (
            <McTable datakey="id" data={filteredRejected} columns={rejectedTableColumns} recordsperpage={20} headersticky />
          )}
        </div>
      </McTabBar>

    

      {showRejectCommentBox && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 33, 50, 0.42)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: 16,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 560,
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 12px 28px rgba(0, 66, 99, 0.2)",
              padding: 18,
            }}
          >
            <h3 style={{ margin: "0 0 10px 0", color: "#003e62", fontSize: 18 }}>
              {pendingRejectMode === "bulk" ? "Reject bulk requests" : "Reject request"}
            </h3>
            <p style={{ margin: "0 0 10px 0", color: "#5b7785", fontSize: 13 }}>
              Provide reason/comments for rejection.
            </p>
            <textarea
              id="rejection-comments"
              value={rejectComment}
              onChange={(event) => setRejectComment(event.target.value)}
              placeholder="Enter rejection comments..."
              style={{
                width: "100%",
                minHeight: 110,
                border: "1px solid #c4d9e4",
                borderRadius: 8,
                padding: "10px 12px",
                fontFamily: '"Maersk Text", Arial, sans-serif',
                fontSize: 14,
                color: "#003e62",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
              <McButton
                label="Cancel"
                appearance="neutral"
                variant="outlined"
                fit="small"
                click={() => {
                  setShowRejectCommentBox(false);
                  setPendingRejectMode(null);
                  setRejectComment("");
                  setError(null);
                }}
              />
              <McButton
                label={pendingRejectMode === "bulk" ? "Confirm reject bulk" : "Confirm reject one"}
                appearance="error"
                variant="filled"
                fit="small"
                click={handleConfirmReject}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
